import { useMemo, useState } from "react";
import type { BaselineKind } from "@trackmark/core";
import { endpoints } from "../api.js";
import { useResource } from "../useResource.js";
import type { AlbumRow } from "./AlbumRankings.js";
import { Empty, Failed, Loading } from "./Loading.js";
import { Rankings, type SongRow } from "./Rankings.js";

/**
 * Rankings, reading the library rather than a 3,418-row fixture.
 *
 * Three requests in parallel because they are three independent tables and the tabs switch
 * between them instantly — waiting on albums to render songs would make the first paint the
 * slowest of the three rather than the fastest.
 */
export function RankingsScreen() {
  /**
   * The MOVE baseline. Off by default: a comparison against a date nobody chose is exactly the
   * guessing §I3 removed, and the column is inserted when asked for rather than always present.
   */
  const [baseline, setBaseline] = useState<BaselineKind | null>(null);
  const baselines = useResource(() => endpoints.baselines(), []);
  const at = baselines.data?.baselines.find((b) => b.kind === baseline)?.at ?? null;

  // Re-fetches when the baseline changes: the as-of ranks are computed over the same filtered
  // population as today's, which only the server can do.
  const songs = useResource(() => endpoints.rankingSongs(at), [at]);
  const artists = useResource(() => endpoints.rankingArtists(), []);
  const albums = useResource(() => endpoints.rankingAlbums(true), []);

  const songRows = useMemo<SongRow[]>(
    () =>
      (songs.data?.songs ?? []).map((s) => ({
        id: s.id,
        title: s.title,
        artist: s.artist,
        artistId: s.artistId,
        album: s.album,
        albumId: s.albumId,
        year: s.year,
        score: s.score,
        ratedAt: s.ratedAt,
        art: s.art,
        orderKey: s.orderKey ?? null,
        previousRank: s.previousRank ?? null,
        isNew: s.isNew ?? false,
      })),
    [songs.data],
  );

  const albumRows = useMemo<AlbumRow[]>(
    () =>
      (albums.data?.albums ?? []).map((a) => ({
        id: a.id,
        title: a.title,
        artist: a.artist,
        year: a.year,
        primaryType: a.primaryType,
        genre: a.genre,
        cohesion: a.cohesion,
        art: a.art,
        tracks: a.tracks,
      })),
    [albums.data],
  );

  const error = songs.error ?? artists.error ?? albums.error;
  if (error) {
    return (
      <Failed
        error={error}
        retry={() => {
          songs.reload();
          artists.reload();
          albums.reload();
        }}
      />
    );
  }

  if (songs.loading || artists.loading || albums.loading) return <Loading what="rankings" />;

  if (songRows.length === 0) {
    return <Empty title="Nothing to rank yet" hint="IMPORT A LIBRARY, THEN PRESS R" />;
  }

  return (
    <Rankings
      songs={songRows}
      albums={albumRows}
      artists={artists.data!.artists}
      collection={artists.data!.collection}
      settings={artists.data!.settings}
      baselines={baselines.data?.baselines ?? []}
      baseline={baseline}
      onBaseline={setBaseline}
      onOrder={async (tier, songIds) => (await endpoints.orderTier(tier, songIds)).orderKeys}
    />
  );
}
